import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { fetchAiInsightsEnabled } from '../../components/model/rgipChatApi';
import { legacyForced } from '../../components/layout/legacy-mode';
import OverviewEnhanced from './OverviewEnhanced';
import OverviewLegacy from './OverviewLegacy';

type MatchParams = {
  tenant: string;
  modelId: string;
};

// The model Overview page is behind the AI insights flag (studio-ai-model-insights,
// the same flag that gates the chat rail). Tenants WITHOUT it see the original
// page exactly as it is on master (OverviewLegacy); tenants WITH it get the
// rebuilt two-column layout (OverviewEnhanced). Resolve the answer before mounting
// either page, so only one runs its data-loading lifecycle (both fetch on mount) and
// the layout never flashes from one to the other. The flag lookup is cached per tab
// (see rgipChatApi), so this costs one request, and fail-closed to the legacy page.
//
// The classic-experience opt-out is applied HERE rather than inside
// fetchAiInsightsEnabled, which is where the equivalent lives for the revamp flag
// (fetchTreeInsightsEnabled). That helper's other two consumers are RootLayout and
// ModelChatPanel, which are the chat rail: an AI surface, deliberately outside the
// opt-out. This page is not one. What it renders is the layout revamp, so somebody
// who picked the classic experience gets the classic page and keeps the rail.
function OverviewGate({ match }: RouteComponentProps<MatchParams>) {
  const { tenant } = match.params;
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Never read during render: the app is server-rendered and storage does not
    // exist on the server, so a render that reads it cannot match the server's
    // markup. Same reason ModelPage resolves it in componentDidMount.
    const answer = legacyForced(tenant)
      ? Promise.resolve(false)
      : fetchAiInsightsEnabled(tenant);
    answer
      .then((value) => {
        if (!cancelled) setEnabled(value);
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenant]);

  if (enabled === null) return null; // resolving — mount neither page yet
  return enabled ? <OverviewEnhanced /> : <OverviewLegacy />;
}

export default withRouter(OverviewGate);
